// The last release that resolved completely, kept so a learner can start the
// app when Appwrite is unreachable.
//
// Read-through validation, not trust: every read re-parses and re-checks the
// record. IndexedDB is per-origin mutable storage that survives deploys, so a
// record can be from an older build, a half-finished write, or a devtools
// session. A record that does not fully validate is a miss, which costs one
// network round trip and nothing else.

import {
	parseAudioManifest,
	parseCourseBundle,
	type AudioManifest,
	type CourseBundle,
} from "@aybuben/core/content/bundle.ts";
import { contentBundleDigest, audioManifestDigest } from "@aybuben/core/content/bundle.ts";
import { parseReleaseDescriptor, type ReleaseDescriptor } from "@aybuben/core/content/release.ts";
import { contentVersionOf } from "@aybuben/core/content/version.ts";

export interface CachedRelease {
	readonly descriptor: ReleaseDescriptor;
	readonly courseJson: string;
	readonly audioManifestJson: string;
	readonly cachedAt: number;
}

/** Everything a boot needs, or null if the record cannot be trusted. */
export async function revalidateCached(
	r: CachedRelease,
): Promise<{ bundle: CourseBundle; manifest: AudioManifest } | null> {
	let descriptor: ReleaseDescriptor;
	let bundle: CourseBundle;
	let manifest: AudioManifest;
	try {
		descriptor = parseReleaseDescriptor(JSON.stringify(r.descriptor));
		bundle = parseCourseBundle(r.courseJson);
		manifest = parseAudioManifest(r.audioManifestJson);
	} catch {
		return null;
	}
	// This runs on *every* offline load, so it is the only thing standing between
	// a learner and a cache that has been truncated, half-written, or paired with
	// the wrong descriptor. A record that merely parses is not a record that can
	// be trusted: the digests are what prove the bytes are the ones the release
	// names, and without them a stale-but-valid bundle would sail through.
	const courseBytes = new TextEncoder().encode(r.courseJson);
	if ((await contentBundleDigest(courseBytes)) !== descriptor.course.sha256) return null;
	const manifestBytes = new TextEncoder().encode(r.audioManifestJson);
	if ((await audioManifestDigest(manifestBytes)) !== descriptor.audioManifest.sha256) return null;
	// The recomputed version is what proves the descriptor and the bytes describe
	// the same content, so a record that pairs them wrongly is rejected rather
	// than folded under a false version.
	if (contentVersionOf(bundle.data) !== descriptor.course.contentVersion) return null;
	if (bundle.contentVersion !== descriptor.course.contentVersion) return null;
	// A dangling audio reference would be discovered mid-lesson, as a silent
	// failure at the moment the learner presses play. Offline there is no
	// manifest to fall back to, so the release is not shippable as cached.
	for (const item of bundle.data.items) {
		const name = (item as { audio?: unknown }).audio;
		if (name === undefined) continue;
		if (typeof name !== "string" || !manifest.assets[name]) return null;
	}
	return { bundle, manifest };
}

/**
 * One raw record, untrusted: the cache re-validates every read. Methods may
 * reject; ReleaseCache absorbs it (a read is a miss, a write or clear is a no-op).
 */
export interface ReleaseStore {
	read(): Promise<unknown>;
	write(r: CachedRelease): Promise<void>;
	clear(): Promise<void>;
}

export class ReleaseCache {
	constructor(private readonly store: ReleaseStore) {}

	async read(): Promise<CachedRelease | null> {
		let raw: unknown;
		try {
			raw = await this.store.read();
		} catch {
			return null;
		}
		if (!raw || typeof raw !== "object") return null;
		// Safe: only an object reaches here, and every field is shape-checked below.
		const r = raw as CachedRelease;
		// Shape-check the envelope before handing it to the validator, so a record
		// from a future build fails here rather than inside a JSON.parse of
		// undefined.
		if (
			typeof r.courseJson !== "string" ||
			typeof r.audioManifestJson !== "string" ||
			!r.descriptor
		)
			return null;
		return (await revalidateCached(r)) ? r : null;
	}

	// A cache failure must never fail a load that already succeeded.
	async write(r: CachedRelease): Promise<void> {
		try {
			await this.store.write(r);
		} catch {
			// Best effort: the next online load writes again.
		}
	}

	async clear(): Promise<void> {
		try {
			await this.store.clear();
		} catch {
			// Best effort: a stale record is re-validated on every read anyway.
		}
	}
}

export class MemoryReleaseStore implements ReleaseStore {
	private record: CachedRelease | null = null;
	async read(): Promise<unknown> {
		return this.record;
	}
	async write(r: CachedRelease): Promise<void> {
		this.record = r;
	}
	async clear(): Promise<void> {
		this.record = null;
	}
}
